import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { formatTimeAgo } from '../../utils/formatters';
import { useApi } from '../../hooks/useApi';

export function ChatScreen({ requestId, onNavigate }) {
  const { user } = useAuth();
  const api = useApi();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    async function fetchMessages() {
      setLoading(true);
      try {
        const res = await api.messages.list(requestId);
        if (!cancelled) setMessages(res.data || []);
      } catch (err) {
        console.error('Messages fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchMessages();
    // Poll for new messages every 10s
    const interval = setInterval(fetchMessages, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [requestId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      await api.messages.send({
        request_id: requestId,
        content: input.trim(),
      });
      setInput('');
      // Refresh messages
      const res = await api.messages.list(requestId);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <button
          onClick={() => onNavigate('request-detail', requestId)}
          style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', fontSize: '16px' }}
        >
          ←
        </button>
        <h3 style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>
          Skilaboð
        </h3>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: theme.textMuted, padding: '40px' }}>Hleð...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textSecondary, padding: '40px' }}>
            <p style={{ fontSize: '32px', marginBottom: '8px' }}>💬</p>
            <p style={{ fontSize: '14px' }}>Engin skilaboð enn</p>
          </div>
        ) : (
          messages.map(msg => {
            const isOwn = msg.sender_id === user?.sub;
            return (
              <div key={msg.id} style={{
                alignSelf: isOwn ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
              }}>
                <div style={{
                  background: isOwn ? theme.accent : theme.bgCard,
                  color: isOwn ? '#000' : theme.text,
                  padding: '10px 14px',
                  borderRadius: '16px',
                  borderBottomRightRadius: isOwn ? '4px' : '16px',
                  borderBottomLeftRadius: isOwn ? '16px' : '4px',
                  fontSize: '14px',
                  lineHeight: 1.4,
                  border: isOwn ? 'none' : `1px solid ${theme.border}`,
                }}>
                  {msg.content}
                </div>
                <p style={{
                  fontSize: '10px',
                  color: theme.textMuted,
                  marginTop: '2px',
                  textAlign: isOwn ? 'right' : 'left',
                }}>
                  {formatTimeAgo(msg.created_at)}
                </p>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input Bar */}
      <div style={{
        padding: '12px 16px',
        borderTop: `1px solid ${theme.border}`,
        display: 'flex',
        gap: '8px',
        background: theme.bgCard,
      }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Skrifa skilaboð..."
          style={{
            flex: 1,
            padding: '10px 14px',
            background: theme.bgInput,
            border: `1px solid ${theme.border}`,
            borderRadius: '20px',
            color: theme.text,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: input.trim() ? theme.accent : theme.border,
            color: input.trim() ? '#000' : theme.textMuted,
            border: 'none',
            fontSize: '18px',
            cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
